import {
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Spin,
  DatePicker,
  TimePicker,
} from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import moment from "moment";


const InputRenderers = ({
  item,
  type,
  disableCondition,
  isCalculatedField,
  loadingCalculation,
  handleInputChange,
  handleSelectInputChange,
  handleTextInputChange,
  debouncedCalculation,
  className = '',
  countryCode,
  countrySettings,
}) => {
  const inputClassName = `cmt-input mt-10 ${className}`.trim();
  const selectClassName = `cmt-select mt-10 ${className}`.trim();
  const radioClassName = `cmt-radio mt-10 ${className}`.trim();
  const numberClassName = `cmt-input-number mt-10 ${className}`.trim();

  if (['select', 'select_multiple'].includes(item.input_type)) {
    const normalizedOptions = Array.isArray(item.options)
      ? item.options
      : (item.options && typeof item.options === 'string') ? item.options.split(',').map(option => option.trim()) : [];

    let initialValue;

    if (item.result) {
      const resultArray = Array.isArray(item.result)
        ? item.result
        : typeof item.result === 'string' ? item.result.split(',').map(value => value.trim()) : [];

      initialValue = item.input_type === 'select_multiple'
        ? resultArray
        : resultArray[0];
    } else {
      const defaultOption = item.default_value ||
        (normalizedOptions[0]?.value?.toString() ?? (typeof normalizedOptions[0] === 'object' ? '' : normalizedOptions[0]));

      initialValue = item.input_type === 'select_multiple'
        ? (Array.isArray(defaultOption) ? defaultOption : [defaultOption])
        : defaultOption;
    }

    return (
      <Form.Item
        name={[type.input_name, item.id.toString()]}
        initialValue={initialValue}
      >
        <Select
          mode={item.input_type === 'select_multiple' && 'multiple'}
          className={selectClassName}
          placeholder={
            item.default_value ? item.default_value : `Seleccione ${item.input_type === 'select' ? 'opción' : 'opcion/es'}`
          }
          disabled={disableCondition}
          allowClear
          options={normalizedOptions?.map((option) => ({
            label: option?.text ?? (typeof option === 'object' ? '' : option),
            value: option?.value?.toString() ?? (typeof option === 'object' ? '' : option),
          }))}
          onChange={(value) => {
            handleSelectInputChange?.(value, item);
            if (!isCalculatedField) {
              debouncedCalculation?.(item, value);
            }
          }}
        />
      </Form.Item>
    );
  }

  if (['text', 'textArea'].includes(item.input_type)) {
    return (
      <Spin className="cmt-spin" spinning={loadingCalculation}>
        <Form.Item name={[type.input_name, item.id.toString()]} initialValue={item.result || item.default_value}>
          <Input.TextArea
            className={inputClassName}
            placeholder={item.default_value ? item.default_value : 'Ingrese texto'}
            style={{ width: '100%', height: 100 }}
            disabled={disableCondition || isCalculatedField}
            onChange={(event) => handleTextInputChange?.(event.target.value, item)}
          />
        </Form.Item>
      </Spin>
    );
  }

  if (item.input_type === 'string') {
    return (
      <Spin className="cmt-spin" spinning={loadingCalculation}>
        <Form.Item name={[type.input_name, item.id.toString()]} initialValue={item.result || item.default_value}>
          <Input
            className={inputClassName}
            placeholder={item.default_value ?? 'Ingrese dato'}
            style={{ width: '100%' }}
            disabled={disableCondition || isCalculatedField}
            onChange={(event) => handleTextInputChange?.(event.target.value, item)}
          />
        </Form.Item>
      </Spin>
    );
  }

  if (item.input_type === 'date') {
    return (
      <Spin className="cmt-spin" spinning={loadingCalculation}>
        <Form.Item
          name={[type.input_name, item.id.toString()]}
          initialValue={(() => {
            const resultDate = moment(item.result, 'DD-MM-YYYY');
            if (item.result && resultDate.isValid()) {
              return resultDate;
            }
            return null;
          })()}
        >
          <DatePicker
            className="date-picker mt-10"
            locale={locale}
            placeholder={item.default_value ?? 'Seleccione fecha'}
            popupClassName="custom-dropdown"
            style={{ width: '100%' }}
            format="DD-MM-YYYY"
            disabled={disableCondition || isCalculatedField}
            onChange={() => {
              handleInputChange?.(null, item);
            }}
          />
        </Form.Item>
      </Spin>
    );
  }

  if (item.input_type === 'time') {
    return (
      <Spin className="cmt-spin" spinning={loadingCalculation}>
        <Form.Item
          name={[type.input_name, item.id.toString()]}
          initialValue={(() => {
            const resultTime = moment(item.result, 'HH:mm');
            if (item.result && resultTime.isValid()) {
              return resultTime;
            }
            const defaultTime = moment(item.default_value, 'HH:mm');
            if (item.default_value && defaultTime.isValid()) {
              return defaultTime;
            }
            return null;
          })()}
        >
          <TimePicker
            className="time-picker mt-10"
            locale={locale}
            placeholder={item.default_value ?? 'Seleccione hora'}
            popupClassName="custom-dropdown"
            style={{ width: '100%' }}
            format="HH:mm"
            disabled={disableCondition || isCalculatedField}
            onChange={() => {
              handleInputChange?.(null, item);
            }}
          />
        </Form.Item>
      </Spin>
    );
  }

  if (['integer', 'float'].includes(item.input_type)) {
    return (
      <Spin className="cmt-spin" spinning={loadingCalculation}>
        <Form.Item name={[type.input_name, item.id.toString()]} initialValue={item.result || item.default_value}>
          <InputNumber
            code={item.code}
            className={numberClassName}
            placeholder={item.default_value ?? 'Ingrese valor'}
            style={{ width: '100%' }}
            min={item.restrict_min && item.result ? item.result - item.restrict_min : null}
            max={item.restrict_max && item.result ? item.result - item.restrict_max : null}
            disabled={disableCondition || isCalculatedField}
            onChange={(value) => {
              if (!isCalculatedField) {
                debouncedCalculation?.(item, value);
              }
            }}
          />
        </Form.Item>
      </Spin>
    );
  }

  if (item.input_type === 'radio') {
    const normalizedOptions = Array.isArray(item.options)
      ? item.options
      : (item.options && typeof item.options === 'string') ? item.options.split(',').map(option => option.trim()) : [];
    let initialValue;

    if (item.result && typeof item.result === 'string' && item.result.trim()) {
      const resultArray = Array.isArray(item.result)
        ? item.result
        : item.result.split(',').map(result => result.trim());
      initialValue = resultArray[0];
    } else {
      initialValue = item.default_value ||
        (normalizedOptions[0]?.value?.toString() ?? (typeof normalizedOptions[0] === 'object' ? '' : normalizedOptions[0]));
    }

    return (
      <Form.Item name={[type.input_name, item.id.toString()]} initialValue={initialValue}>
        <Radio.Group
          className={radioClassName}
          disabled={disableCondition}
          options={normalizedOptions.map((option) => ({
            label: option?.text ?? (typeof option === 'object' ? '' : option),
            value: option?.value?.toString()?.trim() ?? (typeof option === 'object' ? '' : option),
          }))}
        />
      </Form.Item>
    );
  }

  return null;
};

export default InputRenderers;
